 import { Check, X, Minus, Scale } from "lucide-react";
 import { useTranslation } from "@/i18n";
 import {
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableHeader,
   TableRow,
 } from "@/components/ui/table";
 
 const CompareTable = () => {
   const { t } = useTranslation();
 
   const features = [
     {
       key: "generatesContent",
       rankaro: true,
       yoast: false,
       rankmath: false,
       aioseo: false,
     },
     {
       key: "bulkProcessing",
       rankaro: true,
       yoast: false,
       rankmath: false,
       aioseo: false,
     },
     {
      key: "integratedAI",
      rankaro: true,
      yoast: "premium",
      rankmath: "premium",
      aioseo: "premium",
     },
     {
       key: "detectsChanges",
       rankaro: true,
       yoast: false,
       rankmath: false,
       aioseo: false,
     },
     {
       key: "longDescriptions",
       rankaro: true,
       yoast: false,
       rankmath: false,
       aioseo: false,
     },
    {
      key: "seoPluginCompat",
      rankaro: true,
      yoast: "na",
      rankmath: "na",
      aioseo: "na",
    },
    {
      key: "antiCannibalization",
      rankaro: true,
      yoast: false,
      rankmath: false,
      aioseo: false,
    },
     {
       key: "industryProfiles",
       rankaro: true,
       yoast: false,
       rankmath: false,
       aioseo: false,
     },
     {
       key: "autoRedirects",
       rankaro: true,
       yoast: "premium",
       rankmath: true,
       aioseo: "premium",
     },
     {
       key: "focusKeyphrase",
       rankaro: true,
       yoast: false,
       rankmath: false,
       aioseo: false,
     },
     {
       key: "categorySeo",
       rankaro: true,
       yoast: false,
       rankmath: false,
       aioseo: false,
     },
   ];
 
   const renderValue = (value: boolean | string) => {
     if (value === true) {
       return (
         <div className="flex items-center justify-center">
           <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
             <Check className="w-4 h-4 text-primary" />
           </div>
         </div>
       );
     }
     if (value === false) {
       return (
         <div className="flex items-center justify-center">
           <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
             <X className="w-4 h-4 text-muted-foreground" />
           </div>
         </div>
       );
     }
     if (value === "premium") {
       return (
         <span className="text-xs font-medium text-accent px-2 py-1 rounded-full bg-accent/10">
           {t('compare.premium')}
         </span>
       );
     }
     if (value === "na") {
       return (
         <div className="flex items-center justify-center">
           <Minus className="w-4 h-4 text-muted-foreground/50" />
         </div>
       );
     }
     return null;
   };
 
   return (
     <section id="compare" className="py-20 md:py-32 relative overflow-hidden">
       {/* Background */}
       <div className="absolute inset-0 pointer-events-none">
         <div className="glow-orb glow-orb-accent w-[500px] h-[500px] top-0 right-0 opacity-15" />
         <div className="glow-orb glow-orb-primary w-[400px] h-[400px] bottom-0 left-0 opacity-20" />
       </div>
 
       <div className="container mx-auto px-4 relative">
         <div className="max-w-3xl mx-auto text-center mb-12">
           <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium mb-6">
             <Scale size={16} className="text-accent" />
             <span className="text-foreground">{t('compare.badge')}</span>
           </div>
           <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
             {t('compare.title')}
           </h2>
           <p className="text-lg text-muted-foreground">
             {t('compare.subtitle')}
           </p>
         </div>
 
         {/* Comparison Table */}
         <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl border border-border/30 overflow-x-auto">
              <Table className="min-w-[500px]">
               <TableHeader>
                 <TableRow className="border-border/30 hover:bg-transparent">
                   <TableHead className="text-foreground font-semibold w-[200px]">
                     {t('compare.feature')}
                   </TableHead>
                   <TableHead className="text-center">
                     <div className="flex flex-col items-center gap-1">
                       <span className="font-bold text-primary text-lg">Rankaro</span>
                     </div>
                   </TableHead>
                   <TableHead className="text-center">
                     <span className="font-medium text-muted-foreground">Yoast SEO</span>
                   </TableHead>
                   <TableHead className="text-center">
                     <span className="font-medium text-muted-foreground">RankMath</span>
                   </TableHead>
                   <TableHead className="text-center">
                     <span className="font-medium text-muted-foreground">All in One SEO</span>
                   </TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {features.map((feature) => (
                   <TableRow key={feature.key} className="border-border/30">
                     <TableCell className="font-medium text-foreground">
                       {t(`compare.features.${feature.key}`)}
                     </TableCell>
                     <TableCell className="bg-primary/5">
                       {renderValue(feature.rankaro)}
                     </TableCell>
                     <TableCell>{renderValue(feature.yoast)}</TableCell>
                     <TableCell>{renderValue(feature.rankmath)}</TableCell>
                     <TableCell>{renderValue(feature.aioseo)}</TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
           </div>
 
           {/* Clarification note */}
           <div className="mt-8 p-6 rounded-2xl glass-card border border-primary/20 text-center">
             <p className="text-foreground">
               <strong className="text-primary">{t('compare.importantTitle')}</strong>{" "}
               {t('compare.importantText')}
             </p>
           </div>
         </div>
       </div>
     </section>
   );
 };
 
 export default CompareTable;